import {
  SelectChangeEvent,
  Grid,
  Fade,
  Typography,
  Divider,
  Autocomplete,
} from "@mui/material";
import { ChangeEvent, useEffect, useState, useContext } from "react";
import { ClientProps, WellProps } from "../../interfaces/interfaces";
import DataContext from "../../context/DataContext";
import {
  StyledTextField,
  StyledFormButton,
  StyledFormContainer,
  StyledFormSection,
} from "../../utils/formStyles";

interface Props {
  data?: WellProps;
  client?: ClientProps;
  onEditWell?: (values: WellProps) => void;
  onAddWell?: (values: WellProps) => void;
  onCancel: () => void;
}

const initial_form_state = {
  client: { id: 0, name: "" },
  name: "",
  contact: "",
  phoneNumber: "",
};

export const AddWellForm = ({
  data,
  client,
  onAddWell,
  onEditWell,
  onCancel,
}: Props) => {
  const [values, setValues] = useState(
    data || { ...initial_form_state, client },
  );
  const [toSave, setToSave] = useState(false);
  const { clientOptions } = useContext(DataContext);
  useEffect(() => {
    if (values.name !== "") setToSave(true);
  }, [values]);

  const onChangeValue = (
    event:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<number>,
  ) => {
    setValues((prevState) => {
      return {
        ...prevState,
        [event.target.name]: event.target.value,
      };
    });
  };

  const onChangeClient = (newClient: ClientProps) => {
    setValues((prevState) => {
      return {
        ...prevState,
        client: newClient,
      };
    });
  };

  return (
    <Fade in timeout={600}>
      <StyledFormContainer>
        <StyledFormSection>
          <Typography
            variant="h5"
            component="h2"
            sx={{ color: "primary.main", fontWeight: 600, mb: 1 }}
          >
            Well Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={3}>
            {client ? (
              <Grid item lg={6} xs={12}>
                <StyledTextField
                  type="text"
                  label="Client"
                  variant="outlined"
                  value={client.name}
                  disabled
                  fullWidth
                />
              </Grid>
            ) : (
              <Grid item lg={6} xs={12}>
                <Autocomplete<ClientProps>
                  options={clientOptions}
                  getOptionLabel={(option: ClientProps) => option.name}
                  value={values.client}
                  onChange={(event: any, newValue: ClientProps | null) =>
                    newValue && onChangeClient(newValue)
                  }
                  renderOption={(props: any, option: ClientProps) => (
                    <li {...props} key={option.id}>
                      {option.name}
                    </li>
                  )}
                  disablePortal
                  renderInput={(params: any) => (
                    <StyledTextField {...params} label="Client" />
                  )}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      borderRadius: 1,
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(90, 100, 119, 0.15)",
                      },
                    },
                  }}
                />
              </Grid>
            )}
            <Grid item lg={6} xs={12}>
              <StyledTextField
                type="text"
                label="Well Name"
                name="name"
                variant="outlined"
                value={values.name}
                onChange={onChangeValue}
                fullWidth
                required
              />
            </Grid>
            <Grid item lg={6} xs={12}>
              <StyledTextField
                type="text"
                label="Contact Name"
                variant="outlined"
                name="contact"
                value={values.contact}
                onChange={onChangeValue}
                fullWidth
              />
            </Grid>
            <Grid item lg={6} xs={12}>
              <StyledTextField
                type="text"
                label="Phone Number"
                name="phoneNumber"
                variant="outlined"
                value={values.phoneNumber}
                onChange={onChangeValue}
                fullWidth
              />
            </Grid>
          </Grid>
        </StyledFormSection>

        <Grid container spacing={2} justifyContent="center" sx={{ mt: 2 }}>
          <Grid item>
            <StyledFormButton
              onClick={onCancel}
              variant="outlined"
              sx={{ minWidth: 120 }}
            >
              Cancel
            </StyledFormButton>
          </Grid>
          {onAddWell && (
            <Grid item>
              <StyledFormButton
                onClick={() => onAddWell(values)}
                variant="contained"
                disabled={!toSave}
                sx={{ minWidth: 120 }}
              >
                Add Well
              </StyledFormButton>
            </Grid>
          )}
          {onEditWell && (
            <Grid item>
              <StyledFormButton
                onClick={() => onEditWell(values)}
                variant="contained"
                disabled={!toSave}
                sx={{ minWidth: 120 }}
              >
                Update Well
              </StyledFormButton>
            </Grid>
          )}
        </Grid>
      </StyledFormContainer>
    </Fade>
  );
};
